
import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useKeycloakContext } from '@/contexts/KeycloakContext';
import { useUserRole } from '@/hooks/useUserRole';
import { toast } from '@/hooks/use-toast';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children,
  requiresAdmin = false 
}) => {
  const { user, isLoading: authLoading, isAuthenticated, signIn } = useKeycloakContext();
  const { role, isLoading: roleLoading } = useUserRole();
  
  const isLoading = authLoading || roleLoading;

  useEffect(() => {
    // Debug information to console
    console.log('Protected route check:', { 
      isAuthenticated, 
      role, 
      requiresAdmin,
      authLoading,
      roleLoading,
      user: user ? { 
        sub: user.sub, 
        roles: user.roles,
        email: user.email
      } : null
    });
    
    if (!isLoading && requiresAdmin && role !== 'admin') {
      toast({
        title: "访问被拒绝",
        description: "您需要管理员权限才能访问此页面",
        variant: "destructive"
      });
    }
  }, [isAuthenticated, role, requiresAdmin, isLoading, user]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  // For development purposes, temporarily allow access to admin pages
  // DEVELOPMENT ONLY - REMOVE IN PRODUCTION
  if (process.env.NODE_ENV === 'development' && requiresAdmin) {
    
    return <>{children}</>;
  }

  // For admin routes, require both authentication and admin role
  if (requiresAdmin) {
    if (!isAuthenticated) {
      
      signIn();
      return null;
    }
    
    if (role !== 'admin') {
      
      return <Navigate to="/" replace />;
    }
  }

  // For regular protected routes, just require authentication
  if (!isAuthenticated && !requiresAdmin) {
    
    signIn();
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
